import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link as RouterLink } from "react-router-dom";

/**
 * Confirms removing one or more documents from a prompt.
 *
 * Removal only detaches the docs from the current prompt; the files always stay
 * in the library (Add from library) and can be re-attached later. Permanent
 * deletion lives on the All Files page, the single destructive surface.
 *
 * Props:
 *  - open
 *  - documents: array of {id, fileName}
 *  - onClose
 *  - onConfirm: () => Promise<void>
 *  - resourceNoun: "document" | "reference"
 */
const BulkDeleteConfirmDialog = ({
  open,
  documents = [],
  onClose,
  onConfirm,
  resourceNoun = "document",
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const count = documents.length;
  const noun = count === 1 ? resourceNoun : `${resourceNoun}s`;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm?.();
      // Parent closes us on success.
    } catch (err) {
      console.error("[BulkDeleteConfirmDialog] confirm failed:", err);
      setError(err?.message || `Failed to remove ${noun}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Remove {count} {noun} from this prompt?
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
        <DialogContentText>
          The {count === 1 ? "file stays" : "files stay"} in your library and can
          be re-attached with &quot;Add from library&quot;. To permanently delete
          files, please use the{" "}
          <Link component={RouterLink} to="/dashboard/files">
            All Files
          </Link>{" "}
          page.
        </DialogContentText>

        {count <= 10 ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              {documents.map((doc) => doc.fileName || "Untitled").join(", ")}
            </Typography>
          </Box>
        ) : null}

        {error ? <Alert severity="error">{error}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          onClick={handleConfirm}
          loading={submitting}
        >
          Remove
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteConfirmDialog;
